import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Clock,
  CheckCircle,
  XCircle,
  FileText,
  TrendingUp,
  ArrowRight,
} from 'lucide-react';

import { Layout } from '../components/layout/Layout';
import { Card } from '../components/ui/Card';
import { customerAPI } from '../services/ApiServices';
import { useAuth } from '../contexts/AuthContext';

/* =========================
   Helpers
========================= */
const normalizeStats = (apiData, role) => {
  if (role === 'admin') {
    const result = {
      total: apiData?.total || 0,
      approved: 0,
      overridden: 0,
      rejected: 0,
      pending: 0,
    };

    apiData?.stats?.forEach((item) => {
      switch (item._id) {
        case 'APPROVED':
          result.approved = item.count;
          break;
        case 'APPROVE_AND_OVERRIDE':
          result.overridden = item.count;
          break;
        case 'REJECTED':
          result.rejected = item.count;
          break;
        default:
          result.pending += item.count;
      }
    });

    return result;
  }

  // KYC USER
  return {
    total: apiData?.total || 0,
    approved: apiData?.approved || 0,
    overridden: apiData?.overridden || 0,
    rejected: apiData?.rejected || 0,
    pending: apiData?.pending || 0,
  };
};

/* =========================
   Component
========================= */
export const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = user?.role?.toLowerCase() || '';

  const [stats, setStats] = useState(null);
  const [recentLogs, setRecentLogs] = useState([]);
  const [adminData, setAdminData] = useState(null);
  const [kycData, setKycData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const response = await customerAPI.getDashboardStats(role);
        const data = response.data;

        if (role === 'admin') {
          setAdminData(data);
          console.log('Admin Dashboard Data:', data);
        } else {
          setKycData(data);
          console.log('KYC User Dashboard Data:', data);
        }
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboard();
  }, [role]);

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  /* =========================
     Dashboard Cards
  ========================= */

  const statCards =
    role === 'admin'
      ? [
        {
          title: 'Pending',
          value: adminData?.overview?.pending || 0,
          icon: Clock,
          lightBg: 'bg-yellow-50',
          textColor: 'text-yellow-600',
          path: '/pending',
        },
        {
          title: 'Approve Only',
          value:
            adminData?.approvalTypeBreakdown?.APPROVE_ONLY || 0,
          icon: CheckCircle,
          lightBg: 'bg-blue-50',
          textColor: 'text-blue-600',
          path: '/resolved',
        },
        {
          title: 'Approve & Override',
          value:
            adminData?.approvalTypeBreakdown?.APPROVE_AND_OVERRIDE || 0,
          icon: CheckCircle,
          lightBg: 'bg-purple-50',
          textColor: 'text-purple-600',
          path: '/approved-overridden',
        },
        {
          title: 'Rejected',
          value: adminData?.overview?.rejected || 0,
          icon: XCircle,
          lightBg: 'bg-red-50',
          textColor: 'text-red-600',
          path: '/rejected',
        },
        {
          title: 'Total',
          value: adminData?.overview?.totalVerifications || 0,
          icon: FileText,
          lightBg: 'bg-blue-50',
          textColor: 'text-blue-600',
          path: './',
        },
      ]
      : [
        {
          title: 'Pending',
          value: kycData?.stats?.pending || 0,
          icon: Clock,
          lightBg: 'bg-yellow-50',
          textColor: 'text-yellow-600',
          path: '/pending',
        },
        {
          title: 'Approved',
          value: kycData?.stats?.approved || 0,
          icon: CheckCircle,
          lightBg: 'bg-green-50',
          textColor: 'text-green-600',
          path: '/resolved',
        },
        {
          title: 'Approve & Override',
          value: kycData?.stats?.approveWithOverride || 0,
          icon: CheckCircle,
          lightBg: 'bg-purple-50',
          textColor: 'text-purple-600',
          path: '/approved-overridden',
        },
        {
          title: 'Rejected',
          value: kycData?.stats?.rejected || 0,
          icon: XCircle,
          lightBg: 'bg-red-50',
          textColor: 'text-red-600',
          path: '/rejected',
        },
        {
          title: 'Total',
          value: kycData?.stats?.totalReviewed || 0,
          icon: FileText,
          lightBg: 'bg-blue-50',
          textColor: 'text-blue-600',
          path: './',
        },
      ];

  /* =========================
     Rates
  ========================= */
  const completionRate =
    role === 'admin'
      ? (adminData?.overview?.approvalRate || 0) +
      (adminData?.overview?.rejectionRate || 0)
      : (kycData?.summary?.approvalRate || 0) +
      (kycData?.summary?.rejectionRate || 0);

  const approvalRate =
    role === 'admin'
      ? adminData?.overview?.approvalRate || 0
      : kycData?.summary?.approvalRate || 0;

  const rejectionRate =
    role === 'admin'
      ? adminData?.overview?.rejectionRate || 0
      : kycData?.summary?.rejectionRate || 0;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600 mt-2">
            Welcome to your KYC verification portal
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          {statCards.map((stat) => {
            const Icon = stat.icon;

            return (
              <div
                key={stat.title}
                onClick={() => navigate(stat.path)}
                className="cursor-pointer transition-all hover:scale-105"
              >
                <Card className="hover:shadow-lg h-full flex flex-col justify-between">

                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600 mb-1 truncate">
                        {stat.title}
                      </p>
                      <p className="text-4xl font-bold text-gray-900">
                        {stat.value}
                      </p>
                    </div>

                    <div className={`${stat.lightBg} p-3 rounded-xl`}>
                      <Icon className={`w-6 h-6 ${stat.textColor}`} />
                    </div>
                  </div>

                  <div className="flex items-center gap-2 mt-4 text-sm font-medium text-gray-500 hover:text-gray-700">
                    View Details <ArrowRight className="w-4 h-4" />
                  </div>

                </Card>
              </div>
            );
          })}
        </div>

        {/* Bottom Section */}
        <div className={`grid gap-6 ${role === 'admin' ? 'lg:grid-cols-2' : ''}`}>

          {/* Quick Stats */}
          <Card>
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-blue-50 p-2 rounded-lg">
                <TrendingUp className="w-5 h-5 text-blue-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">
                Quick Stats
              </h2>
            </div>

            <div className="space-y-3">
              <div className="flex justify-between py-2 border-b">
                <span className="text-sm text-gray-600">
                  Completion Rate
                </span>
                <span className="font-semibold">
                  {completionRate}%
                </span>
              </div>

              <div className="flex justify-between py-2 border-b">
                <span className="text-sm text-gray-600">
                  Approval Rate
                </span>
                <span className="font-semibold text-green-600">
                  {approvalRate}%
                </span>
              </div>

              <div className="flex justify-between py-2">
                <span className="text-sm text-gray-600">
                  Rejection Rate
                </span>
                <span className="font-semibold text-red-600">
                  {rejectionRate}%
                </span>
              </div>
            </div>
          </Card>

          {/* Admin: Daily Trends */}
          {role === 'admin' && (
            <Card>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Daily Trends
              </h2>

              <div className="space-y-3">
                {adminData?.dailyTrends?.length ? (
                  adminData.dailyTrends.slice(0, 5).map((trend) => (
                    <div
                      key={trend._id}
                      className="p-3 rounded-lg bg-blue-50"
                    >
                      <p className="text-sm font-semibold">
                        {trend._id}
                      </p>
                      <p className="text-xs text-gray-600">
                        Total: {trend.total}
                      </p>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500">
                    No daily trends available
                  </p>
                )}
              </div>
            </Card>
          )}

          {/* KYC User: Recent Activity */}
          {role === 'kyc_user' && (
            <Card>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Recent Activity
              </h2>

              <div className="space-y-3">
                {kycData?.recentActivity?.length ? (
                  kycData.recentActivity.slice(0, 5).map((item) => (
                    <div
                      key={item.verificationId}
                      className="p-3 rounded-lg bg-blue-50"
                    >
                      <p className="text-sm font-medium">
                        Account: {item.accountNumber}
                      </p>
                      <p className="text-xs text-gray-600">
                        Status: {item.status}
                      </p>
                      <p className="text-xs text-gray-500">
                        Mismatches: {item.mismatchCount}
                      </p>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500">
                    No recent activity
                  </p>
                )}
              </div>
            </Card>
          )}
        </div>
      </div>
    </Layout>
  );
};
